// Task cards — a 5-column, Trello-style board of every task card, one column
// per mastery sack. Compact preview tiles live in the columns (draggable
// between columns exactly like MasteryBoard.tsx's `data-bin-dropzone`
// mechanism); clicking one expands the real, unmodified TaskCard.tsx —
// flip, reattempt, move-to-green, review, all of it — into a centered
// overlay via a framer-motion shared layoutId, so it visually grows out of
// its column into the middle of the screen.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useAnimationControls, type PanInfo } from 'framer-motion';
import type { BinColor, TaskCardDTO } from '@/types';
import { useTaskCards } from '@/hooks/useTaskCards';
import { BIN_LABEL, BIN_NOTE, BIN_ORDER, BIN_VAR_KEY } from '@/lib/bins';
import { TaskCard } from '@/components/dashboard/TaskCard';
import { MathText } from '@/components/exam/MathText';
import { Icon } from '@/components/garden';
import { StudentPage, StudentPageHeader } from '@/components/ds';

function isBetterBin(target: BinColor, current: BinColor): boolean {
  return BIN_ORDER.indexOf(target) < BIN_ORDER.indexOf(current);
}

interface CardTileProps {
  card: TaskCardDTO;
  hidden: boolean;
  onOpen: () => void;
  onMoveToGreen: (id: string) => Promise<unknown>;
  onMoveToBin: (id: string, bin: BinColor) => Promise<unknown>;
  onDragActive: (active: boolean) => void;
}

function CardTile({
  card,
  hidden,
  onOpen,
  onMoveToGreen,
  onMoveToBin,
  onDragActive,
}: CardTileProps) {
  const controls = useAnimationControls();
  const key = BIN_VAR_KEY[card.currentBin];

  // Single source of truth for this tile's opacity/position — handles the
  // mount-in pop AND the fade when its expanded modal is open, without
  // fighting a plain `style.opacity` for the same property.
  useEffect(() => {
    controls.start({ opacity: hidden ? 0 : 1, y: 0, scale: 1, transition: { duration: 0.2 } });
  }, [hidden, controls]);

  async function handleDragEnd(_e: unknown, info: PanInfo) {
    onDragActive(false);
    const el = document.elementFromPoint(info.point.x, info.point.y);
    const zone = el?.closest('[data-bin-dropzone]');
    const targetBin = zone?.getAttribute('data-bin-dropzone') as BinColor | null;
    if (!targetBin || targetBin === card.currentBin) return;

    if (isBetterBin(targetBin, card.currentBin)) {
      // Upward moves have to be earned, not self-reported — open the full
      // card so the student can actually reattempt it (same rule TaskCard.tsx
      // enforces for its own drag-and-drop).
      onOpen();
      return;
    }
    try {
      if (targetBin === 'GREEN') await onMoveToGreen(card.id);
      else await onMoveToBin(card.id, targetBin);
    } catch {
      await controls.start({ x: [0, -8, 8, -6, 6, 0], transition: { duration: 0.3 } });
    }
  }

  return (
    <motion.div
      layoutId={`taskcard-${card.id}`}
      layout
      drag
      dragSnapToOrigin
      onDragStart={() => onDragActive(true)}
      onDragEnd={handleDragEnd}
      initial={{ opacity: 0, y: 8, scale: 0.96 }}
      animate={controls}
      whileTap={{ scale: 0.97 }}
      whileDrag={{ scale: 1.04, zIndex: 50, cursor: 'grabbing' }}
      onClick={onOpen}
      className="rounded-xl border-l-4 p-3 text-left hover:shadow-md"
      style={{
        borderLeftColor: `var(--${key}-border)`,
        background: 'var(--surface-card)',
        boxShadow: 'var(--shadow-sm)',
        cursor: 'pointer',
        pointerEvents: hidden ? 'none' : 'auto',
      }}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="truncate text-fs-11 font-bold" style={{ color: `var(--${key}-text)` }}>
          {card.subtopicName}
        </span>
        <span
          className="flex-none text-fs-11 font-semibold"
          style={{ color: 'var(--ink-3)' }}
          data-numeric=""
        >
          {card.earnedMarks}/{card.maxMarks}
        </span>
      </div>
      <div className="line-clamp-2 text-fs-13" style={{ color: 'var(--text-body)' }}>
        <MathText>{card.questionText || 'Question'}</MathText>
      </div>
    </motion.div>
  );
}

export function TaskCardsPage() {
  const navigate = useNavigate();
  // The open card, and the column it was opened from. While it is open its
  // tile stays in that column even if the card changes sack underneath it — a
  // correct reattempt moves a Pink card to Green with the overlay still up.
  // Moving the tile then would mount a SECOND element with the overlay's
  // layoutId in another column, and framer-motion would hand the shared layout
  // to the new tile: the open card was projected into the column behind a
  // backdrop that still swallowed every click, which read as a frozen screen.
  // So the move waits until the card is closed, then animates as one.
  const [expanded, setExpanded] = useState<{ id: string; bin: BinColor } | null>(null);
  const expandedId = expanded?.id ?? null;
  const [dragging, setDragging] = useState(false);
  const { data: cards, isLoading, moveToGreen, review, moveToBin, reattempt } = useTaskCards();

  const byBin = useMemo(() => {
    const map: Record<BinColor, TaskCardDTO[]> = {
      GREEN: [],
      PURPLE: [],
      BLUE: [],
      ORANGE: [],
      PINK: [],
    };
    for (const c of cards ?? []) {
      map[c.id === expanded?.id ? expanded.bin : c.currentBin].push(c);
    }
    return map;
  }, [cards, expanded]);

  const expandedCard = (cards ?? []).find((c) => c.id === expandedId) ?? null;

  const moveToGreenFn = (id: string) => moveToGreen.mutateAsync(id);
  const moveToBinFn = (id: string, bin: BinColor) =>
    moveToBin.mutateAsync({ cardId: id, targetBin: bin });

  return (
    <StudentPage>
      <StudentPageHeader
        title="Cards"
        subtitle="Drag a card to another sack, or click one to open it fully."
        action={
          <button
            onClick={() => navigate('/learn/basics')}
            className="inline-flex flex-none items-center gap-2 rounded-full px-4 py-2 text-fs-13 font-bold transition"
            style={{ border: '1px solid var(--border-strong)', color: 'var(--text-body)' }}
          >
            <Icon name="sprout" size={14} /> Open scaffold
          </button>
        }
      />

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {BIN_ORDER.map((b) => (
            <div key={b} className="h-64 animate-pulse rounded-2xl bg-paper-sunk" />
          ))}
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2 lg:grid lg:grid-cols-5 lg:overflow-visible">
          {BIN_ORDER.map((bin) => {
            const list = byBin[bin];
            const key = BIN_VAR_KEY[bin];
            return (
              <div
                key={bin}
                data-bin-dropzone={dragging ? bin : undefined}
                className="flex min-h-[220px] w-[260px] flex-none flex-col gap-2.5 rounded-2xl p-3 lg:w-auto"
                style={{
                  background: 'var(--paper-sunk)',
                  border: dragging
                    ? `2px dashed var(--${key}-border)`
                    : '1px solid var(--border-default)',
                }}
              >
                <div className="mb-0.5 flex items-center justify-between px-1">
                  <div>
                    <div className="text-fs-13 font-bold" style={{ color: `var(--${key}-text)` }}>
                      {BIN_LABEL[bin]}
                    </div>
                    <div className="text-fs-10.5 font-medium" style={{ color: 'var(--ink-3)' }}>
                      {BIN_NOTE[bin]}
                    </div>
                  </div>
                  <span
                    className="flex-none rounded-full px-2 py-0.5 text-fs-11 font-bold"
                    style={{ background: `var(--${key}-bg)`, color: `var(--${key}-text)` }}
                    data-numeric=""
                  >
                    {list.length}
                  </span>
                </div>

                <div className="flex flex-col gap-2">
                  {list.length === 0 ? (
                    <p className="px-1 py-6 text-center text-xs" style={{ color: 'var(--ink-3)' }}>
                      {dragging ? `Drop here to move to ${BIN_LABEL[bin]}` : 'No cards here.'}
                    </p>
                  ) : (
                    list.map((card) => (
                      <CardTile
                        key={card.id}
                        card={card}
                        hidden={card.id === expandedId}
                        onOpen={() => setExpanded({ id: card.id, bin: card.currentBin })}
                        onMoveToGreen={moveToGreenFn}
                        onMoveToBin={moveToBinFn}
                        onDragActive={setDragging}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Expanded card — grows from its tile into the middle of the screen. */}
      <AnimatePresence>
        {expandedCard && (
          <>
            <motion.div
              key="expand-backdrop"
              className="fixed inset-0 z-40"
              style={{ background: 'rgba(0,0,0,.5)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setExpanded(null)}
            />
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-6"
              style={{ pointerEvents: 'none' }}
            >
              <motion.div
                layoutId={`taskcard-${expandedCard.id}`}
                className="w-full max-w-2xl overflow-y-auto"
                style={{ pointerEvents: 'auto', maxHeight: '90vh' }}
              >
                <TaskCard
                  card={expandedCard}
                  onMoveToGreen={moveToGreenFn}
                  onMoveToBin={moveToBinFn}
                  onReview={(id, recalled) => review.mutate({ cardId: id, recalled })}
                  onReattempt={(id, selectedIdx) =>
                    reattempt.mutateAsync({ cardId: id, selectedIdx })
                  }
                />
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </StudentPage>
  );
}
